import { DetailReturnContext } from './components/detail/DetailReturnState';
import { useBrowseSession, useBrowsePosition } from '../../features/browse/useBrowseList';
import { correctAlbumRegion } from './components/album/albumRegionCorrection';
import AlbumDetailHeader from './components/album/AlbumDetailHeader';
import { useRememberContent, recentAlbum } from '../../features/recent/recentContentStore';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE, REVIEW_SECTION } from '../../constants/gaEnums';
import {
  trackContentSave,
  trackReviewContentClick,
  trackUserProfileClick,
} from '../../lib/gaEvents';
import { Alert, Snackbar } from '@mui/material';
import AlbumLoadState from './components/album/AlbumLoadState';
import AlbumHero from './components/album/AlbumHero';
import AlbumStreamingLinks from './components/album/AlbumStreamingLinks';
import { useUserStore } from '../../stores/userStore';
import {
  useAlbumDetail,
  useAlbumDetailBySlug,
  useManageLatestAlbum,
  useToggleBookmark,
  useToggleHomeAlbumFeature,
  useToggleStarred,
  useUpdateRecommendedAlbumComment,
} from './hooks/useAlbumDetail';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAlbumReviews } from './hooks/useAlbumReviews';
import api from '../../api/api';
import PageLayout from '../../components/layout/PageLayout';
import Tracklist from './components/album/Tracklist';
import AlbumInfoSection, { AlbumAdminActions } from './components/album/AlbumInfoSection';
import MyReviewSection from './components/my-review/MyReviewSection';
import UserReviewSection from './components/user-review/UserReviewSection';
import AlbumSecondaryContent from './components/album/AlbumSecondaryContent';

import SEO from '../../components/common/SEO';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import AlbumNote from './components/album/AlbumNote';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../utils/env';
import AlbumContentPager from './components/album/AlbumContentPager';
import './components/album/AlbumDetailLayout.css';

const useSpotifyUrl = (albumName, artistName, albumId, enabled = true) =>
  useQuery({
    queryKey: ['spotifyUrl', albumName, artistName, albumId],
    queryFn: async () => {
      const res = await api.get('/albums/spotify-url', {
        params: { album_name: albumName, artist_name: artistName, album_id: albumId },
        suppressGlobalError: true,
      });
      return res.data?.data?.spotify_url || null;
    },
    enabled: enabled && !!albumName && !!artistName,
    staleTime: 1000 * 60 * 60 * 24, // 24 hour
    retry: false,
  });

const ReviewDetail = ({ entry, returning, registrationNoticeOpen, setRegistrationNoticeOpen }) => {
  const queryClient = useQueryClient();
  const actionsRef = useRef(null);
  const params = useParams();
  const location = useLocation();
  const albumSlug = params.albumSlug;
  const paramSource = params.albumSource;
  const paramId = params.sourceId;
  const { user, isAdmin } = useUserStore();
  // 슬러그 기반 또는 기존 ID 기반 조회 (두 훅 모두 항상 호출, 각각 enabled 조건으로 제어)
  const slugResult = useAlbumDetailBySlug(albumSlug);
  const {
    data: directAlbumData,
    isLoading: directLoading,
    error: directError,
    refetch: retryDirect,
  } = useAlbumDetail(paramSource, paramId);

  const albumSource = albumSlug ? slugResult.albumSource : paramSource;
  const sourceId = albumSlug ? slugResult.sourceId : paramId;
  const albumData = albumSlug ? slugResult.data : directAlbumData;
  const albumLoading = albumSlug ? slugResult.isLoading : directLoading;
  useRememberContent(recentAlbum(albumData), {
    ready: !albumLoading,
    error: albumSlug ? slugResult.error : directError,
  });

  useTrackPageView(
    {
      pageType: PAGE_TYPE.REVIEWS_POST,
      contentType: CONTENT_TYPE.ALBUM,
      contentId: sourceId,
      contentName: albumData?.title,
    },
    { ready: !!sourceId },
  );

  const toggleBookmark = useToggleBookmark(albumSource, sourceId);
  const toggleStarred = useToggleStarred(albumSource, sourceId);
  const toggleHomeAlbumFeature = useToggleHomeAlbumFeature(albumSource, sourceId);
  const updateRecommendedAlbumComment = useUpdateRecommendedAlbumComment(albumSource, sourceId);
  const manageLatestAlbum = useManageLatestAlbum(albumSource, sourceId);
  const reviewsQuery = useAlbumReviews(albumSource, sourceId);
  const loadedReviews = reviewsQuery.data;
  const allReviews = loadedReviews ?? albumData?.reviews ?? [];

  // Spotify URL을 albumInfo와 병렬로 가져오기 (캐시된 URL이 없을 때만)
  const needsSpotifyFetch = albumData && !albumData.urls?.spotify;
  const { data: spotifyUrl } = useSpotifyUrl(
    albumData?.title,
    albumData?.artist?.name,
    albumData?.id,
    needsSpotifyFetch,
  );

  // albumInfo에 spotifyUrl 병합
  const albumInfo = useMemo(() => {
    if (!albumData) return null;
    if (!needsSpotifyFetch || !spotifyUrl) return albumData;
    return {
      ...albumData,
      urls: {
        ...albumData.urls,
        spotify: spotifyUrl,
      },
    };
  }, [albumData, needsSpotifyFetch, spotifyUrl]);

  const position = useBrowsePosition({
    entry,
    returning,
    ready: Boolean(albumInfo) && !albumLoading,
  });
  const isLoading = albumLoading;
  const albumError = albumSlug ? slugResult.error : directError;
  const retryAlbum = albumSlug ? slugResult.retry : retryDirect;

  const currentAlbumId = useMemo(() => {
    if (!albumInfo) return null;
    return albumInfo.id ?? (albumSource === 'local' ? sourceId : null);
  }, [albumInfo, albumSource, sourceId]);

  const recommendationComment = albumInfo?.recommendationComment?.trim();

  const handleLatestAlbumRegionMove = (displayRegion) => {
    manageLatestAlbum.mutate({ albumInfo, action: 'region', displayRegion });
  };

  const handleLatestAlbumHide = () => {
    manageLatestAlbum.mutate({ albumInfo, action: 'hide' });
  };

  const handleLatestAlbumRegister = () => {
    manageLatestAlbum.mutate({ albumInfo, action: 'register' });
  };

  const handleRecommendedAlbumRemove = () => {
    manageLatestAlbum.mutate({ albumInfo, action: 'removeRecommended' });
  };

  const renderRecommendationNote = (sx = {}) => {
    if (!recommendationComment) return null;

    return <AlbumNote comment={recommendationComment} sx={sx} />;
  };

  return (
    <PageLayout paddingTop={0}>
      {!albumInfo && <AlbumDetailHeader />}
      <Snackbar
        open={registrationNoticeOpen}
        autoHideDuration={3500}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setRegistrationNoticeOpen(false)}
      >
        <Alert severity="success" variant="filled" onClose={() => setRegistrationNoticeOpen(false)}>
          앨범 등록이 완료되었습니다.
        </Alert>
      </Snackbar>
      {albumInfo && albumInfo.artist && (
        <>
          <OpenInAppBanner
            type="album"
            title={albumInfo.title}
            subtitle={`${albumInfo.artist.name} · 앨범`}
            image={albumInfo.imageUrl}
          />
          <SEO
            title={`${albumInfo.artist.name} - ${albumInfo.title} 앨범 리뷰`}
            description={`${albumInfo.title} by ${albumInfo.artist.name} 발매일: ${albumInfo.releaseDate?.split('.')[0] || ''}. 장르: ${albumInfo.genres?.[0] || ''}`}
            image={albumInfo.imageUrl}
            url={
              albumSlug
                ? `${SITE_URL}/reviews/${albumSlug}`
                : `${SITE_URL}/reviews/${albumSource}/${sourceId}`
            }
            rawTitle
          />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'Reviews',
                    item: `${SITE_URL}/reviews`,
                  },
                  { '@type': 'ListItem', position: 3, name: albumInfo.title },
                ],
              })}
            </script>
          </Helmet>
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'MusicAlbum',
                name: albumInfo.title,
                image: albumInfo.imageUrl,
                url: albumSlug
                  ? `${SITE_URL}/reviews/${albumSlug}`
                  : `${SITE_URL}/reviews/${albumSource}/${sourceId}`,
                datePublished: albumInfo.releaseDate
                  ? albumInfo.releaseDate.replace(
                      /(\d+)\.(\d+)\.(\d+)/,
                      (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                    )
                  : undefined,
                genre: albumInfo.genres?.length ? albumInfo.genres : undefined,
                numTracks: albumInfo.totalTracks || undefined,
                byArtist: {
                  '@type': 'MusicGroup',
                  name: albumInfo.artist.name,
                },
                ...(albumInfo.reviewCount > 0 && {
                  aggregateRating: {
                    '@type': 'AggregateRating',
                    ratingValue: albumInfo.averageRating.toFixed(1),
                    bestRating: '5',
                    worstRating: '0.5',
                    ratingCount: String(albumInfo.reviewCount),
                  },
                }),
                ...(allReviews.length > 0 && {
                  review: allReviews.slice(0, 3).map((r) => ({
                    '@type': 'Review',
                    author: { '@type': 'Person', name: r.user.nickname },
                    reviewRating: {
                      '@type': 'Rating',
                      ratingValue: r.rating,
                      bestRating: 5,
                      worstRating: 0.5,
                    },
                    datePublished: r.createdAt
                      ? r.createdAt.replace(
                          /(\d+)\.(\d+)\.(\d+)/,
                          (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                        )
                      : undefined,
                  })),
                }),
              })}
            </script>
          </Helmet>
        </>
      )}
      <AlbumLoadState loading={isLoading} error={albumError} album={albumInfo} onRetry={retryAlbum}>
        {albumInfo && (
          <div
            {...position}
            className={`pp-album-detail pp-album-detail-tabs${recommendationComment ? ' has-note' : ''}`}
          >
            <AlbumHero
              key={`hero:${albumSource}:${sourceId}`}
              album={albumInfo}
              reviews={allReviews}
              adminActions={
                isAdmin && (
                  <AlbumAdminActions
                    albumInfo={albumInfo}
                    isAdmin={isAdmin}
                    onStarredToggle={() => toggleStarred.mutate({ albumInfo })}
                    onHomeLatestToggle={() =>
                      toggleHomeAlbumFeature.mutate({ albumInfo, target: 'latest' })
                    }
                    onHomeRecommendedToggle={(recommendationComment, recommendationCommentEn) =>
                      toggleHomeAlbumFeature.mutate({
                        albumInfo,
                        target: 'recommended',
                        recommendationComment,
                        recommendationCommentEn,
                      })
                    }
                    onRecommendedCommentUpdate={(recommendationComment, recommendationCommentEn) =>
                      updateRecommendedAlbumComment.mutate({
                        albumInfo,
                        recommendationComment,
                        recommendationCommentEn,
                      })
                    }
                    onLatestAlbumRegionMove={handleLatestAlbumRegionMove}
                    onLatestAlbumHide={handleLatestAlbumHide}
                    onLatestAlbumRegister={handleLatestAlbumRegister}
                    onRecommendedAlbumRemove={handleRecommendedAlbumRemove}
                  />
                )
              }
            />
            <div className="pp-album-actions">
              <AlbumInfoSection
                albumInfo={albumInfo}
                actionRef={actionsRef}
                headerOptions={{
                  isAdmin,
                  onRegionChange: async (displayRegion) => {
                    if (useUserStore.getState().user?.id !== user?.id || !isAdmin)
                      return Promise.reject(new Error('Account changed'));
                    await correctAlbumRegion(albumInfo.id, displayRegion);
                    await Promise.all(
                      [
                        'albumDetail',
                        'reviewsAlbums',
                        'browse-reviews',
                        'home',
                        'web-discovery',
                        'relatedAlbums',
                        'search',
                      ].map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
                    );
                  },
                }}
                reviews={allReviews}
                bookmarkPending={toggleBookmark.isPending}
                pageType={PAGE_TYPE.REVIEWS_POST}
                onBookmarkToggle={() => {
                  const wasBookmarked = albumInfo.isBookmarked;
                  return toggleBookmark.mutateAsync(
                    { albumInfo, currentAlbumId },
                    {
                      onSuccess: (data) => {
                        if (!wasBookmarked) {
                          trackContentSave(
                            {
                              contentType: CONTENT_TYPE.ALBUM,
                              contentId: sourceId,
                              contentName: albumInfo?.title,
                              pageType: PAGE_TYPE.REVIEWS_POST,
                            },
                            user,
                          );
                        }
                      },
                    },
                  );
                }}
              />
            </div>
            {recommendationComment && (
              <div className="pp-album-note">{renderRecommendationNote()}</div>
            )}
            <AlbumContentPager
              initialTab={
                entry.albumTab ?? (location.state?.recordTrackRating ? 'tracks' : 'reviews')
              }
              onTabChange={(tab) => {
                if (entry.current()) entry.albumTab = tab;
              }}
              key={`content:${albumSource}:${sourceId}`}
              reviews={
                <div className="pp-album-discussion">
                  <MyReviewSection
                    album={albumData}
                    reviews={allReviews}
                    onCompose={() => actionsRef.current?.compose()}
                    onRemove={() => actionsRef.current?.remove()}
                    historicalAlbumId={albumData?.hasMyHistoricalReviews ? albumData.id : null}
                    albumSource={albumSource}
                    sourceId={sourceId}
                    albumTitle={albumInfo?.title}
                  />
                  <UserReviewSection
                    reviews={allReviews}
                    key={`${albumSource}:${sourceId}:${user?.id ?? 'guest'}`}
                    myReviewId={
                      user && albumData?.myReview?.content?.trim()
                        ? albumData.myReview.id
                        : undefined
                    }
                    loading={reviewsQuery.isLoading && !albumData?.reviews}
                    error={reviewsQuery.isError && !loadedReviews && !albumData?.reviews}
                    onRetry={reviewsQuery.refetch}
                    onReviewOpen={(review) =>
                      trackReviewContentClick(
                        {
                          section: REVIEW_SECTION.USER_REVIEWS,
                          contentType: CONTENT_TYPE.REVIEW,
                          contentId: review.id,
                        },
                        user,
                      )
                    }
                    onAuthorOpen={(review) =>
                      trackUserProfileClick(
                        { contentName: review.user?.nickname, pageType: PAGE_TYPE.REVIEWS_POST },
                        user,
                      )
                    }
                  />
                  {/* Build58 SHOW_CRITIC_REVIEWS=false: do not mount/query this section. */}
                </div>
              }
              tracks={
                <>
                  <Tracklist tracks={albumInfo.tracks} album={albumInfo} showHeading={false} />
                  <AlbumStreamingLinks album={albumInfo} />
                </>
              }
            />
            <AlbumSecondaryContent album={albumInfo} />
          </div>
        )}
      </AlbumLoadState>
    </PageLayout>
  );
};

export default function AlbumDetailRoute() {
  const memory = useBrowseSession('album-detail');
  const location = memory.location;
  const navigate = useNavigate();
  // Keep the success notice outside the history-keyed content: clearing its URL state
  // replaces the entry but must not immediately dismiss the existing notice.
  const [registrationNoticeOpen, setRegistrationNoticeOpen] = useState(
    Boolean(location.state?.directCatalogCreated),
  );
  useEffect(() => {
    if (!location.state?.directCatalogCreated) return;
    setRegistrationNoticeOpen(true);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state?.directCatalogCreated, navigate]);
  return (
    <DetailReturnContext.Provider value={memory}>
      <ReviewDetail
        key={`${memory.location.key}:${memory.entry.version}`}
        {...memory}
        registrationNoticeOpen={registrationNoticeOpen}
        setRegistrationNoticeOpen={setRegistrationNoticeOpen}
      />
    </DetailReturnContext.Provider>
  );
}
